/*=============================================== FooterPage ===============================================*/

import { useContext } from "react"
import { Footer, CodeContainer, Text } from "tsx-library-julseb"
import type { FooterItemProps } from "tsx-library-julseb/types"

import { LangContext } from "context"
import type { LangContextType } from "context"

import { ComponentsLayout } from "components"
import type { ItemType, PropsItemType } from "components"

import { propAllLinksClickRequired } from "data"

const footerLinks: FooterItemProps[] = [
    {
        content: "Link",
        href: "#",
    },
    {
        content: "Router Link",
        to: "/",
    },
    {
        content: "Button",
        onClick: () => console.log("Clicked!"),
    },
    {
        content: "Button",
        onClick: () => console.log("Clicked!"),
    },
]

const FooterPage = () => {
    const { language } = useContext(LangContext) as LangContextType

    const items: ItemType[] = [
        {
            title: "Without separator",
            content: (
                <Footer
                    logo={{
                        img: "/images/logo-fajny-css.svg",
                        width: 100,
                        height: 32,
                    }}
                    items={footerLinks}
                />
            ),
            code: '<Footer\n    logo={{ img: "/images/logo-fajny-css.svg", width: 100, height: 32 }}\n    items={footerLinks}\n/>',
        },
        {
            title: "With separator",
            content: (
                <Footer
                    logo={{ text: "Logo" }}
                    separator
                    items={footerLinks}
                    direction="vertical"
                />
            ),
            code: '<Footer\n    logo={{ text: "Logo" }}\n    separator\n    items={footerLinks}\n    direction="vertical"\n/>',
        },
    ]

    const introRequired = (
        <>
            If <code>direction</code> is set to <code>vertical</code>, either{" "}
            <code>items</code> or <code>logo</code> is required. If it's set to{" "}
            <code>horizontal</code>, both of them are required.
        </>
    )

    const introImg = (
        <>
            Only if <code>img</code> is defined.
        </>
    )

    const propsItems: PropsItemType[] = [
        {
            subtitle: "separator",
            type: "boolean",
        },
        {
            subtitle: "direction",
            type: "string",
            possible: ["horizontal", "vertical"],
            default: "horizontal",
        },
        "separator",
        {
            subtitle: "items",
            intro: introRequired,
            type: "array of objects",
        },
        {
            title: (
                <>
                    <code>item</code> object
                </>
            ),
            subtitle: "content",
            type: "string or JSX Element",
            required: true,
        },
        ...propAllLinksClickRequired,
        "separator",
        {
            subtitle: "logo",
            intro: introRequired,
            type: "object",
        },
        {
            title: (
                <>
                    <code>logo</code> object
                </>
            ),
            subtitle: "text",
            intro: (
                <>
                    Here, either <code>img</code> or <code>text</code> is
                    required, but only one of them is possible.
                </>
            ),
            type: "string",
        },
        {
            subtitle: "img",
            intro: (
                <>
                    Here, either <code>img</code> or <code>text</code> is
                    required, but only one of them is possible.
                </>
            ),
            type: "string",
        },
        {
            subtitle: "alt",
            intro: introImg,
            type: "string",
            default: "Logo",
        },
        {
            subtitle: "width",
            intro: introImg,
            type: "number",
            default: 100,
        },
        {
            subtitle: "height",
            intro: introImg,
            type: "number",
            default: 30,
        },
    ]

    return (
        <ComponentsLayout items={items} propsItems={propsItems}>
            <Text>For both demos, we are using this array for links:</Text>

            {language === "ts" ? (
                <CodeContainer language="typescript">
                    {
                        'const footerLinks: FooterItemProps[] = [\n    {\n        content: "Link",\n        href: "#",\n    },\n    {\n        content: "Router Link",\n        to: "/",\n    },\n    {\n        content: "Button",\n        onClick: () => console.log("Clicked!"),\n    },\n    {\n        content: "Button",\n        onClick: () => console.log("Clicked!"),\n    },\n]'
                    }
                </CodeContainer>
            ) : (
                <CodeContainer language="javascript">
                    {
                        'const footerLinks = [\n    {\n        content: "Link",\n        href: "#",\n    },\n    {\n        content: "Router Link",\n        to: "/",\n    },\n    {\n        content: "Button",\n        onClick: () => console.log("Clicked!"),\n    },\n    {\n        content: "Button",\n        onClick: () => console.log("Clicked!"),\n    },\n]'
                    }
                </CodeContainer>
            )}
        </ComponentsLayout>
    )
}

export default FooterPage
